import { Button } from '@/components/Button';
import { CheckboxOption } from '@/components/Dropdown';
import { Flex } from '@/components/Grid';
import { Heading } from '@/components/Heading';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Portal } from '@/components/Portal';
import {
  SortKey,
  distances,
  type AdCategoryCountItemType as CategoryType,
} from '@/resource/ad';
import { cx } from '@/util/cx';
import { SetState } from '@/util/types';
import { forwardRef, useEffect, useState } from 'react';
import styles from './Sidebar.module.scss';

type Props = {
  isOpen: boolean;
  sort: SortKey;
  categoryIds: number[];
  distance: number;
  postalCode: string;
  keyword: string;
  categories: CategoryType[];
  setCategories: SetState<CategoryType[]>;
  setUseAllCategories: SetState<boolean>;
  toggleCategory: (category: CategoryType) => void;
  setPostalCode: SetState<string>;
  hasLocation: boolean;
  loading: boolean;
  totalAdCount: number;
  selectedCategoryCount: number;
  useAllCategories: boolean;
  setDistance: (distance: number) => void;
  setSort: (sort: SortKey) => void;
  filterBy: (value: {
    sort: SortKey;
    categoryIds: number[];
    distance: number;
    postalCode: string;
    keyword: string;
  }) => void;
};

const postalCodePattern = /^\d{4}$/;

export const Sidebar = forwardRef<HTMLDivElement, Props>(function Sidebar(
  {
    isOpen,
    categories,
    setCategories,
    totalAdCount,
    useAllCategories,
    setUseAllCategories,
    toggleCategory,
    setDistance,
    setSort,
    setPostalCode,
    hasLocation,
    loading,
    selectedCategoryCount,
    filterBy,
    sort,
    keyword,
    categoryIds,
    distance,
    postalCode,
  }: Props,
  ref
) {
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setOpened(false);
    }
  }, [isOpen]);

  return (
    <Portal>
      <form
        inert={isOpen ? null : ''}
        className={cx(
          styles.Sidebar,
          isOpen && styles['is-open'],
          opened && styles['is-opened']
        )}
        onTransitionEnd={() => setOpened(isOpen)}
        onSubmit={(e) => {
          e.preventDefault();
          filterBy({
            sort: sort || 'recent',
            categoryIds,
            distance,
            postalCode,
            keyword,
          });
        }}
      >
        <div ref={ref} tabIndex={-1}>
          <div className={styles.Inset}>
            <Heading visualLevel={4}>Categorie</Heading>
          </div>

          <Flex dir="column" gap={0}>
            <CheckboxOption
              label="Alle categorieën"
              count={totalAdCount}
              checked={useAllCategories}
              onChange={() => {
                setUseAllCategories(!useAllCategories);
                setCategories((categories) =>
                  categories.map((category) => ({
                    ...category,
                    checked: !useAllCategories,
                  }))
                );
              }}
            />

            {categories.map((category) => (
              <CheckboxOption
                key={category.id}
                checked={category.checked}
                onChange={() => toggleCategory(category)}
                label={category.name}
                count={category.count}
              />
            ))}
          </Flex>

          <div className={styles.Inset}>
            <Flex dir="column" gap={2}>
              <Heading visualLevel={4}>Waar?</Heading>

              <Input
                id="postal_code_2"
                type="text"
                placeholder="Postcode"
                minLength={4}
                maxLength={4}
                inputMode="numeric"
                value={postalCode}
                append={
                  hasLocation ? (
                    <Icon icon="check" size={2} theme={3} />
                  ) : undefined
                }
                onChange={(e) => {
                  const { value } = e.target;
                  const isPostalCode = postalCodePattern.test(value);

                  if (!isPostalCode && sort === 'adjacent') {
                    setSort('recent');
                  }

                  if (value === '' || /\d{1,4}/.test(value)) {
                    setPostalCode(value);
                  }
                }}
              />

              <select
                value={distance}
                onChange={(e) => setDistance(Number(e.target.value))}
                disabled={!hasLocation}
              >
                <option value={-1}>Alle afstanden</option>
                {distances.map((dist) => (
                  <option key={dist} value={dist}>
                    Binnen {dist} km
                  </option>
                ))}
              </select>

              <Button
                type="submit"
                theme="secondary"
                align="right"
                disabled={loading || selectedCategoryCount === 0}
              >
                Zoek
              </Button>
            </Flex>
          </div>
        </div>
      </form>
    </Portal>
  );
});
